import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { Row, Form, Col, Input, InputGroup } from 'reactstrap';
import { toast } from 'react-toastify';
import ReactButton from 'components/Buttons/ReactButton';
import Loader from 'components/Loader';
import CustomFieldsInputsForm from '../../../shared/components/CustomFieldsInputsForm';
import TelephoneMask from 'components/Inputs/TelephoneMask';
import { removeMaskSpecialCharacters } from 'views/CRM/shared/utils/phoneNumberHelpers';
import { formatCustomFieldsToSubmit } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { ScreenPrompt } from 'components/ScreenPrompt';
import contactService from '../../services/contactService';
import Select from 'react-select/async';
import { custom_select } from '../../../../../assets/styles/multiple_select';
import { useSearchAccounts } from '../../../Account/hooks/useSearchAccounts';
import { SearchTagClientInput } from 'components/Inputs/SearchTagClientInput';
import { useEntityCustomFieldsByRecordType } from 'views/fieldConfigs/hooks/useCustomFieldsByRecordType';
import { entitiesName } from 'views/CRM/shared/utils/entitiesName';
import { formatCustomFieldsToDisplay } from 'views/CRM/shared/helpers/customFieldsHelpers';
import { maskPhoneNumber } from 'views/CRM/shared/utils/phoneNumberHelpers';

const InputsForContacts = ({
  recordTypeId,
  contact,
  setSelectedClient = () => {},
  closeModal = () => {},
  editContact = false
}) => {
  const { control, handleSubmit, errors, setValue, reset, formState, watch } =
    useForm();
  const { isDirty } = formState;
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isSearching, filterAccounts } = useSearchAccounts();
  const [account, setAccount] = useState();
  const { customFields } = useEntityCustomFieldsByRecordType(
    entitiesName.CONTACT,
    recordTypeId
  );

  useEffect(() => {
    const account = contact?.account;
    setAccount({
      label: account?.name,
      value: account?.id
    });

    const tags = contact.tags?.map((tag) => ({
      label: tag.name,
      value: tag.id
    }));

    setTags(tags);

    const contactData = {
      ...contact,
      phone: maskPhoneNumber(contact.phone)
    };

    const formattedContact = formatCustomFieldsToDisplay(
      contactData,
      customFields
    );

    reset(formattedContact);
  }, [contact.id, recordTypeId, reset, customFields]);

  const onSubmit = async (data, tagsToUpdate) => {
    data.accountId = account && account.value ? Number(account.value) : null;

    const dataDTO = formatCustomFieldsToSubmit(data, customFields);
    dataDTO.phone = removeMaskSpecialCharacters(data.phone);

    const tagsDTO = tagsToUpdate?.map((tag) => tag.value);
    dataDTO.tags = tagsDTO;

    contactService
      .updateContact(recordTypeId, contact.id, dataDTO)
      .then(() => {
        toast.success('Contato atualizado com successo.', {
          autoClose: 3000,
          closeOnClick: true
        });
        if (editContact) {
          setSelectedClient({
            contactId: contact?.id,
            contactName: dataDTO?.name
          });
          return closeModal();
        }
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.message ??
          'Houve um erro ao atualizar o contato';
        toast.error(msg, {
          autoClose: 3000,
          closeOnClick: true
        });
      });
  };

  const errorFormMessage = (message) => (
    <p style={{ color: 'red' }}>{message}</p>
  );

  return (
    <>
      <ScreenPrompt
        when={isDirty && !formState.isSubmitting}
        message={`Está certo de que deseja sair? Terá que preencher os dados novamente quando retornar`}
      />
      <Form
        className="needs-validation"
        onSubmit={handleSubmit((data) => onSubmit(data, tags))}
      >
        <>
          <div className="mt-3">
            <h2>Dados do contato</h2>
            {loading ? (
              <Loader />
            ) : (
              <>
                <Row>
                  <Col className="mb-3" sm="6">
                    <label className="form-control-label text-capitalize">
                      Nome*
                    </label>
                    <Controller
                      render={(props) => (
                        <Input
                          valueController={setValue}
                          value={props.value}
                          onChange={props.onChange}
                          type="text"
                          placeholder="Nome"
                        />
                      )}
                      control={control}
                      defaultValue=""
                      rules={{ required: 'Campo obrigatório' }}
                      name="name"
                    />
                    <ErrorMessage
                      errors={errors}
                      name="name"
                      render={({ message }) => errorFormMessage(message)}
                    />
                  </Col>
                  <Col>
                    <label className="form-control-label text-capitalize">
                      Telefone
                    </label>
                    <Controller
                      render={(props) => (
                        <InputGroup>
                          <TelephoneMask
                            valueController={setValue}
                            fieldName="phone"
                            value={props.value}
                            placeholder="(88) 99999-9999"
                          />
                        </InputGroup>
                      )}
                      control={control}
                      defaultValue=""
                      rules={{
                        validate: (value) => {
                          if (!value) return true;
                          let phoneFormated = value.replace(/\D/g, '');
                          phoneFormated = phoneFormated.replace(/^0/, '');

                          if (
                            phoneFormated.length < 10 &&
                            phoneFormated.length > 0
                          ) {
                            return 'Telefone inválido';
                          }
                          return true;
                        }
                      }}
                      name="phone"
                    />
                    <ErrorMessage
                      errors={errors}
                      name="phone"
                      render={({ message }) => errorFormMessage(message)}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col className="mb-3" sm="6">
                    <label className="form-control-label text-capitalize">
                      Email
                    </label>
                    <Controller
                      render={(props) => (
                        <Input
                          name="email"
                          valueController={setValue}
                          value={props.value}
                          onChange={props.onChange}
                          placeholder="seu-email@mail.com"
                        />
                      )}
                      control={control}
                      defaultValue=""
                      // O campo não é obrigatório mas é necessário validar o email com regex
                      rules={{
                        validate: (value) => {
                          if (!value) return true;

                          const emailRegex =
                            /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;

                          return (
                            emailRegex.test(value) ||
                            'Email com formato incorreto'
                          );
                        }
                      }}
                      name="email"
                    />
                    <ErrorMessage
                      errors={errors}
                      name="email"
                      render={({ message }) => errorFormMessage(message)}
                    />
                  </Col>
                  <Col>
                    <label className="form-control-label text-capitalize">
                      Conta
                    </label>
                    <Select
                      styles={custom_select}
                      placeholder="Busca rápida"
                      isClearable={true}
                      name="accountId"
                      isLoading={isSearching}
                      loadingMessage={() => 'Buscando...'}
                      noOptionsMessage={({ inputValue }) =>
                        !inputValue
                          ? 'Digite para pesquisar'
                          : 'Nenhum resultado encontrado'
                      }
                      loadOptions={async (inputValue) => {
                        const res = await filterAccounts(inputValue);
                        return (
                          res &&
                          res.map((account) => ({
                            label: account.name,
                            value: account.id
                          }))
                        );
                      }}
                      onChange={(data) => {
                        if (data) {
                          const { value, label } = data;
                          setAccount({
                            label,
                            value
                          });
                        } else {
                          setAccount(null);
                        }
                      }}
                      value={
                        account
                          ? {
                              label: account.label,
                              value: account.value
                            }
                          : ''
                      }
                      defaultValue={
                        account
                          ? {
                              label: account.label,
                              value: account.value
                            }
                          : ''
                      }
                    />
                  </Col>
                </Row>
                <Row>
                  <Col className="mb-3" sm="8">
                    <SearchTagClientInput
                      setValueSelected={setTags}
                      defaultValue={tags}
                      isMulti={true}
                      isDisabled={false}
                    />
                  </Col>
                </Row>

                <Row>
                  <CustomFieldsInputsForm
                    fields={customFields}
                    control={control}
                    errors={errors}
                    watch={watch}
                    isOnEditPage={true}
                    setValue={setValue}
                    Controller={Controller}
                    options={{
                      showCallIconOnPhoneField: true,
                      showMessageIconOnPhoneField: true
                    }}
                    contactMessageClientName={contact?.name}
                  />
                </Row>
              </>
            )}

            <ReactButton btnColor="confirmation" type="submit">
              Salvar
            </ReactButton>
          </div>
        </>
      </Form>
    </>
  );
};

export default InputsForContacts;
